import {
  Flex,
  Text,
  Button,
  List,
  OverflowText,
  isNullish,
  Alert
} from "@theorgchartdev/storybook";
import { GetConnectorList } from "./DataSourceSection";
import ToolbarPopover from "../../../../../Navigation/ContextualToolbar/ToolbarPopover/ToolbarPopover";
import cx from "classnames";
import {
  connectorFlatFileList,
  IsDataSourceSet,
  GetDataSourceLogo,
  DATA_SOURCES_TEST_PREFIX
} from "@/appComponents/Blocks/Modals/SettingsModal/ChartSettings/DataSourceConfiguration/Utils";
import { useMemo } from "react";
import { appstr } from "@coreapp/utils/appstr";
import { useAppendTableManagement } from "@/appComponents/Blocks/Modals/SettingsModal/ChartSettings/DataSourceConfiguration/DataSources/AppendTable/useAppendTableManagement";
import { GetWSMgr } from "@/Utils/StateLib";
import { IsPositionChart } from "@coreapp/utils/IsPositionChart";
import { usePermissionCheck } from "@/hooks/usePermissionCheck";
import { supportEmailaddr } from "@coreapp/constants";

export const GetSourceLabel = (dataSource, type) => {
  let sourceLabel = "";
  const connectorList = GetConnectorList();
  if (!connectorList[type]) return sourceLabel;
  for (let sourceObj of connectorList[type]) {
    if (sourceObj.command === dataSource) return sourceObj.connector;
  }
  return sourceLabel;
};

export const DataSourceButton = ({
  chartConfig,
  dataTestId = "",
  dataSource,
  clickHandler = () => {},
  type,
  showLabel = true,
  removeBackground,
  imageWidth = 72,
  imageHeight = 72,
  buttonWidth = 96,
  buttonPadding = 4,
  imageOnly = false,
  openCtrlRef = null,
  labelProps = {},
  fillWidth = false,
  layout = "stacked",
  config,
  handleOpenConfigModal = () => {},
  compactStyle = false
}) => {
  const { openTable, appendTables } = useAppendTableManagement(GetWSMgr());
  const photoSrc = GetDataSourceLogo(dataSource);
  const sourceLabel = GetSourceLabel(dataSource, type);
  const wsMgr = GetWSMgr();

  const showTableInfo = config && config["source"] === "Append Table";
  const showSourceName =
    (config && connectorFlatFileList.includes(config["source"])) || (showTableInfo && compactStyle);

  const selectedTable = useMemo(() => {
    let selectedTable = appstr("No Table Selected");
    if (showTableInfo) {
      const table = config?.["override"];
      const tableData = wsMgr?.fOrgInfo?.GetAppendTableByID(table?.tableID);
      if (!isNullish(tableData)) {
        selectedTable = tableData["name"];
      } else if (!isNullish(table)) {
        selectedTable = appstr("Entity not found", { entity: appstr("Table") });
      }
      return selectedTable;
    }
  }, [showTableInfo, config, appendTables]);

  const sourceNameInfo = useMemo(() => {
    let sourceNameInfo = appstr("No File Selected");
    if (showSourceName) {
      if (showTableInfo) {
        sourceNameInfo = selectedTable;
      } else {
        const dir = config["datadir"];
        const file = config["datafile"];

        if (dir && file) {
          sourceNameInfo = compactStyle ? file : `${dir} / ${file}`;
        }
      }
      return sourceNameInfo;
    }
  }, [showSourceName, config]);

  const validTableSelected = useMemo(() => {
    if (showTableInfo) {
      const table = config?.["override"];
      const tableData = wsMgr?.fOrgInfo?.GetAppendTableByID(table?.tableID);
      return !isNullish(tableData);
    }
  }, [selectedTable]);

  let defaultButtonClass = removeBackground
    ? "oc-data-source-button-no-bg"
    : "oc-data-source-button";
  let baseClass = imageOnly ? "oc-data-source-image" : defaultButtonClass;
  let layoutClass = `${baseClass}--${layout}`;

  const selectButton = () => {
    if (clickHandler) clickHandler();
    if (openCtrlRef) openCtrlRef.current?.setOpen(!openCtrlRef?.current?.open);
  };

  const onClick = openCtrlRef ? selectButton : clickHandler;

  const width = fillWidth ? "100%" : `${buttonWidth}px`;

  const fileInfoClass =
    showSourceName || showTableInfo ? "oc-data-source-label-file-info" : undefined;

  return (
    <Flex
      className={cx(baseClass, layoutClass)}
      onClick={onClick}
      style={{ width: width, padding: `${buttonPadding}px` }}
    >
      <Flex
        dataTestId={dataTestId}
        style={{
          width: `${imageWidth}px`,
          height: `${imageHeight}px`,
          borderRadius: "4px"
        }}
        flex={"0 0 auto"}
      >
        <img className="responsiveImage" src={photoSrc} alt="fireSpot" />
      </Flex>
      {showLabel && (
        <Flex
          width={"100%"}
          justifyContent={layout === "stacked" ? "center" : undefined}
          className={cx("oc-data-source-label", fileInfoClass)}
        >
          {!compactStyle && (
            <Text
              size={"small"}
              modifier={"medium"}
              {...labelProps}
              className={cx(
                "oc-text-truncate--2",
                "oc-break-word",
                "oc-center-text",
                layout === "stacked" ? "oc-small-text" : undefined
              )}
            >
              {sourceLabel}
            </Text>
          )}
          {(showSourceName || compactStyle) && (
            <OverflowText
              size={"xsmall"}
              className="ocn-settings-text-tertiary"
              modifier={sourceNameInfo === appstr("No File Selected") ? "italic" : undefined}
            >
              {compactStyle ? `${sourceLabel}${showSourceName ? ": " : ""}` : ""} {sourceNameInfo}
            </OverflowText>
          )}
          {showTableInfo &&
            !compactStyle &&
            (validTableSelected ? (
              <Button
                dataTestId={`${DATA_SOURCES_TEST_PREFIX}-append-table-selected-${type}`}
                onClick={e => {
                  e.stopPropagation();
                  if (openCtrlRef) openCtrlRef.current?.setOpen(false);
                  openTable(config.override.tableID, chartConfig.ID);
                }}
                disabled={!validTableSelected}
                size={"xsmall"}
                modifier={validTableSelected ? "underline" : undefined}
              >
                {selectedTable}
              </Button>
            ) : (
              <Text
                colorModifier={"lightgrey"}
                size={"xsmall"}
                modifier={"italic"}
                disabled={true}
                onClick={e => {
                  e.stopPropagation();
                  if (typeof handleOpenConfigModal === "function") {
                    handleOpenConfigModal();
                  } else {
                    onClick();
                  }
                }}
              >
                {selectedTable}
              </Text>
            ))}
        </Flex>
      )}
    </Flex>
  );
};

export const DataSourceMenu = ({
  chartConfig,
  dataTestId,
  dataSource,
  type,
  options,
  openCtrlRef,
  popoverProps,
  triggerProps,
  listProps
}) => {
  const isSourceSet = IsDataSourceSet(dataSource);
  const isPositionDocument = IsPositionChart(chartConfig);
  const { isSupportLogin } = usePermissionCheck();

  if (!isSourceSet && triggerProps?.hideQuickAdd === true) {
    return undefined;
  }

  const defaultTrigger = isSourceSet ? (
    <DataSourceButton
      chartConfig={chartConfig}
      dataTestId={dataTestId}
      dataSource={dataSource}
      type={type}
      openCtrlRef={openCtrlRef}
      fillWidth={true}
      {...triggerProps}
    />
  ) : (
    <Button
      dataTestId={dataTestId ?? `data-source-select-btn-${type}`}
      modifier={"underline"}
      size={"medium"}
    >
      {appstr("Click here to select data source")}
    </Button>
  );

  const trigger = popoverProps?.trigger ?? defaultTrigger;

  const onClick = postClickCallback => {
    openCtrlRef?.current?.setOpen(false);
    postClickCallback();
  };

  const sourceOptions = options.map(item => ({
    ...item,
    clickHandler: () => onClick(item.clickHandler)
  }));

  const showRestrictedOptions = isPositionDocument && ["append", "merge"].includes(type);
  const positionChartSupportedSources = [
    "CSV",
    "XLS",
    "Append Table",
    "Greenhouse",
    "Lever",
    "Jobvite",
    "ICIMS",
    "ADP Recruit Management",
    "ADP Recruitment",
    "ADP WFN CA Recruitment Module",
    "Ultipro",
    "ocAPI"
  ];

  const supportedOptions = showRestrictedOptions
    ? sourceOptions.filter(option => positionChartSupportedSources.includes(option.value))
    : sourceOptions;

  const unsupportedOptions = showRestrictedOptions
    ? sourceOptions
        .filter(option => !positionChartSupportedSources.includes(option.value))
        .map(option => ({
          ...option,
          disabled: !isSupportLogin,
          clickHandler: () => onClick(option.clickHandler)
        }))
    : [];

  return (
    <ToolbarPopover
      placement={"right"}
      otherPopoverProps={{
        reference: trigger,
        openCtrlRef: openCtrlRef,
        floatingProps: { "data-testid": `${DATA_SOURCES_TEST_PREFIX}-${type}-add-source-popover` },
        ...popoverProps
      }}
      overlayProps={{ className: cx("oc-dynamic-list-size", "oc-overlay-relative") }}
    >
      <Flex direction={"column"} justifyContent={"space-between"} gap={"4px"} width={"100%"}>
        <Text size={"large"} modifier={"medium"}>
          {appstr("Select data source")}
        </Text>

        <List
          showCheck={false}
          options={supportedOptions}
          trigger={trigger}
          value={dataSource}
          grid={true}
          coloredSelection={true}
          gridCols={5}
          maxHeight={"400px"}
          className={"oc-no-horz-scroll"}
          {...listProps}
        />

        {unsupportedOptions.length > 0 && (
          <>
            <Alert severity="info">
              {appstr(
                "Data sources below are not yet supported for this chart type. For assistance, please reach out to "
              )}
              <a href={`mailto:${supportEmailaddr}`}> {supportEmailaddr}</a>
            </Alert>
            <List
              showCheck={false}
              options={unsupportedOptions}
              trigger={trigger}
              value={dataSource}
              grid={true}
              coloredSelection={true}
              gridCols={5}
              maxHeight={"400px"}
              className={"oc-no-horz-scroll"}
              {...listProps}
            />
          </>
        )}
      </Flex>
    </ToolbarPopover>
  );
};
